/**
 * Shareable map state in the URL HASH (e.g. `#map=61.4978/23.7610/9.5/recent,percapita`):
 * viewport plus active layer toggles, so a map view can be linked/shared. The hash — not query
 * params — because the SRP's search-state URL sync owns the query string (unknown params there
 * risk being parsed as refinements or dropped on rewrite), while the hash passes through both
 * SSR routing and the search sync untouched.
 *
 * Deliberately EXCLUDED: the commute origin. It is often the user's home; putting it in the URL
 * would leak it into browser history and analytics pageview URLs. Sharing a commute area stays
 * an explicit choice, not a side effect.
 */

export interface MapUrlHashState {
  center: { lat: number; lng: number };
  zoom: number;
  recentOnly: boolean;
  perCapita: boolean;
}

const HASH_PREFIX = '#map=';

export function buildMapUrlHash(state: MapUrlHashState): string {
  const layers = [state.recentOnly ? 'recent' : null, state.perCapita ? 'percapita' : null]
    .filter(Boolean)
    .join(',');
  const parts = [state.center.lat.toFixed(4), state.center.lng.toFixed(4), state.zoom.toFixed(2)];
  if (layers) parts.push(layers);
  return `${HASH_PREFIX}${parts.join('/')}`;
}

export function parseMapUrlHash(hash: string | null | undefined): MapUrlHashState | null {
  if (!hash || !hash.startsWith(HASH_PREFIX)) return null;
  const parts = hash.slice(HASH_PREFIX.length).split('/');
  if (parts.length < 3) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  const zoom = Number(parts[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
  // Sanity-bound to the map's own reachable space (Finland bbox + padding, plausible zooms) —
  // a mangled shared link falls back to the default view instead of a broken camera.
  if (lat < 55 || lat > 73 || lng < 15 || lng > 36 || zoom < 3 || zoom > 22) return null;
  const layers = new Set((parts[3] || '').split(',').filter(Boolean));
  return {
    center: { lat, lng },
    zoom,
    recentOnly: layers.has('recent'),
    perCapita: layers.has('percapita'),
  };
}
